import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import unifiedApi from '../../utils/unifiedApi';
import { formatLocalDateRange } from '../../utils/scheduleFormat';
import { ConflictList } from './ScheduleVenuesAndDates';

export const getBlockingConflicts = (conflicts = [], replaceExistingGames = false) =>
  conflicts.filter((c) => !(replaceExistingGames && c.isReplaceable));

// The conflict list may be truncated for display, so counts come from the API totals when present.
export const getBlockingConflictCount = (approval = {}, replaceExistingGames = false) => {
  const conflicts = approval.conflicts || [];
  if (replaceExistingGames) {
    return approval.blockingConflictCount ?? getBlockingConflicts(conflicts, true).length;
  }
  return approval.conflictCount ?? conflicts.length;
};

const plural = (count, singular, pluralForm = `${singular}s`) => `${count} ${count === 1 ? singular : pluralForm}`;

const ApproveScheduleDialog = ({ open, onClose, leagueId, draft, onApproved }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [replaceExistingGames, setReplaceExistingGames] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [approvalResult, setApprovalResult] = useState(null);

  useEffect(() => {
    if (open) {
      setReplaceExistingGames(false);
      setSubmitting(false);
      setError(null);
      setApprovalResult(null);
    }
  }, [open]);

  const result = draft?.result || {};
  const approval = draft?.approval || {};
  const replaceableGameCount = approval.replaceableGameCount ?? 0;
  const blockingConflicts = getBlockingConflicts(approval.conflicts || [], replaceExistingGames);
  const blockingConflictCount = getBlockingConflictCount(approval, replaceExistingGames);
  const totalGames = result.totalGames ?? 0;
  const extraGames = result.extraGames || [];
  const extraGameCount = result.extraGameCount ?? extraGames.length;
  const gameCount = totalGames + extraGames.filter((g) => !g.isTbd && g.gameType !== 'Bye').length;
  const tbdGameCount = result.tbdGameCount ?? 0;
  const nothingToCreate = totalGames + extraGameCount === 0;
  const hasDates = Boolean(result.firstGameDate && result.lastGameDate);

  const handleApprove = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const response = await unifiedApi.approveScheduleDraft(leagueId, draft.id, {
        replaceExistingGames: replaceableGameCount > 0 && replaceExistingGames,
      });
      if (!response) {
        setError('This schedule no longer exists. It may have been deleted by another manager.');
        return;
      }
      setApprovalResult(response);
      if (onApproved) onApproved(response);
    } catch (err) {
      setError(err?.message || 'Failed to approve the schedule.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !submitting && onClose()} fullWidth maxWidth="md" fullScreen={isMobile}>
      <DialogTitle>{approvalResult ? 'Schedule approved' : 'Approve schedule'}</DialogTitle>
      <DialogContent dividers>
        {approvalResult ? (
          <Stack spacing={1}>
            <Alert severity="success">
              {`${plural(approvalResult.createdGameCount ?? 0, 'game')} created.`}
            </Alert>
            {(approvalResult.createdPlaceholderCount ?? 0) > 0 && (
              <Typography variant="body2">{`${plural(approvalResult.createdPlaceholderCount, 'TBD game')} added as placeholders.`}</Typography>
            )}
            {(approvalResult.replacedGameCount ?? 0) > 0 && (
              <Typography variant="body2">{`${plural(approvalResult.replacedGameCount, 'existing game')} replaced.`}</Typography>
            )}
            {(approvalResult.removedPlaceholderCount ?? 0) > 0 && (
              <Typography variant="body2">{`${plural(approvalResult.removedPlaceholderCount, 'schedule placeholder')} removed.`}</Typography>
            )}
          </Stack>
        ) : (
          <Stack spacing={2}>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {`${plural(gameCount, 'game')}${tbdGameCount > 0 ? ` and ${plural(tbdGameCount, 'TBD game')}` : ''} will be created`}
              {hasDates ? ` from ${formatLocalDateRange(result.firstGameDate, result.lastGameDate)}.` : '.'}
            </Typography>
            {tbdGameCount > 0 && (
              <Typography variant="body2" color="text.secondary">
                TBD games become schedule placeholders on the league schedule; convert them to games once the teams are known.
              </Typography>
            )}
            <Typography variant="body2" color="text.secondary">
              Approved games are published to the league schedule. This draft can no longer be edited afterwards.
            </Typography>

            {replaceableGameCount > 0 && (
              <FormControlLabel
                control={(
                  <Checkbox
                    checked={replaceExistingGames}
                    onChange={(e) => setReplaceExistingGames(e.target.checked)}
                  />
                )}
                label={`Replace ${plural(replaceableGameCount, 'existing future game')} with no score or match reports`}
              />
            )}
            {(approval.keptGamesInRangeCount ?? 0) > 0 && (
              <Alert severity="info">
                {`${plural(approval.keptGamesInRangeCount, 'existing game')} in this date range will be kept`}
                {replaceExistingGames ? ' (they have scores, reports or appearances).' : '.'}
              </Alert>
            )}
            {(approval.removablePlaceholderCount ?? 0) > 0 && (
              <Alert severity="info">
                {`${plural(approval.removablePlaceholderCount, 'schedule placeholder')} on the same date, time and field as a generated game will be removed. Other placeholders are kept.`}
              </Alert>
            )}

            {blockingConflictCount > 0 && (
              <Stack spacing={1}>
                <Alert severity="error">
                  {`${plural(blockingConflictCount, 'game')} clash with existing games or TBD games on the same field. Change the slots, dates or fields`}
                  {!replaceExistingGames && blockingConflicts.some((c) => c.isReplaceable) ? ', or replace existing games,' : ''}
                  {' before approving.'}
                </Alert>
                <ConflictList conflicts={blockingConflicts} />
              </Stack>
            )}

            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
        {approvalResult ? (
          <Button variant="contained" onClick={onClose}>Done</Button>
        ) : (
          <>
            <Button onClick={onClose} disabled={submitting}>Cancel</Button>
            <Button
              variant="contained"
              color="success"
              onClick={handleApprove}
              disabled={submitting || blockingConflictCount > 0 || nothingToCreate}
              startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : null}
            >
              {submitting ? 'Approving…' : 'Approve'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ApproveScheduleDialog;
